import React, { memo, useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Image } from 'react-bootstrap';

// component
import { Text, Button } from 'Component/Ajaib';
import { ButtonText } from 'Component/Button';
import { InformationBox } from 'Component/InformationBox';
import { FormatRupiah, FormatPercentage } from 'Component/NumberFormat';
import { onDoRequestResendEmail } from 'Redux/auth';
import { SkeletonLoad } from 'Component/Skeleton';
import { AuthContext } from '@context/auth';
import { genericStorage } from '@ajaibid/utils';

// asset
import EYE_OPEN_ICON from 'Icon/icon-eye-open.png';
import EYE_CLOSE_ICON from 'Icon/icon-eye-close.png';
import ENVELOPE_ICON from 'Icon/icon-envelope.png';
import FILE_ICON from 'Icon/icon-file.png';
import CLOCK_ICON from 'Icon/icon-o-clock.png';
import FAILED_KYC_ICON from 'Icon/icon-kyc-failed.png';

// style
import stylesGlobal from '@screens/style';
import styles from './style';
import useEventTracker from '../tracker';

const IS_SHOW_ASSET_CACHE_KEY = 'show_asset_dashboard';

const DashboardInvestmentSummary = ({ isCoinInprogress }) => {
  const dispatch = useDispatch();
  const { homeData, homeDataLoading, kycStatusDataLoading } = useSelector((state) => state.auth);
  const { homePortfolioData } = useSelector((state) => state.profile);
  const { profileTradeData: buyingPowerData } = useSelector((state) => state.profile);
  const isEmailVerified = homeData?.is_email_verified;

  const {
    userNotKycYet,
    stockKycRejected,
    stockKycVerifying,
    stockKycVerified,
    mfKycFailed,
    mfKycRejected,
    mfKycVerifying,
    mfKycVerified,
  } = useContext(AuthContext);

  const [isShowAsset, setIsShowAsset] = useState(
    genericStorage.get(IS_SHOW_ASSET_CACHE_KEY) ?? true
  );
  const { eventStartKyc, eventClickResendEmailVerify } = useEventTracker();

  useEffect(() => {
    genericStorage.set(IS_SHOW_ASSET_CACHE_KEY, isShowAsset);
  }, [isShowAsset]);

  const toggleIsShowAsset = useCallback(() => {
    if (isShowAsset) {
      setIsShowAsset(false);
    } else {
      setIsShowAsset(true);
    }
  });

  const resendEmailVerification = useCallback(() => {
    dispatch(onDoRequestResendEmail({ email: homeData?.email }));
    eventClickResendEmailVerify();
  });

  if (homeDataLoading || kycStatusDataLoading || isEmailVerified == null) {
    return (
      <InformationBox gradient style={{ ...stylesGlobal.dBlock, ...styles.informationBox }}>
        <div>
          <SkeletonLoad height={20} />
        </div>
        <div style={{ marginTop: 8 }}>
          <SkeletonLoad height={20} />
        </div>
        <div style={{ marginTop: 8 }}>
          <SkeletonLoad height={20} />
        </div>
      </InformationBox>
    );
  }

  if (isEmailVerified === false) {
    return (
      <InformationBox
        gradient
        style={{
          ...styles.informationBox,
          ...stylesGlobal.w100,
          ...stylesGlobal.between,
        }}
      >
        <div
          style={{
            ...stylesGlobal.w100,
            ...stylesGlobal.dFlexColumn,
            ...stylesGlobal.top,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <Text size="section" bold color="inherit">
              Selangkah Lagi Jadi Investor!
            </Text>
          </div>
          <div>
            <Text size="sub-section" color="inherit">
              Klik link Ajaib di emailmu&nbsp;
              {homeData?.email ?? ''}
              &nbsp;untuk menyelesaikan verifikasi.
            </Text>
          </div>
          <div style={stylesGlobal.left}>
            <Text size="sub-section" color="inherit">
              Belum terima email?
            </Text>
            &nbsp;&nbsp;
            <Button variant="sentence" onClick={resendEmailVerification}>
              <Text color="yellow">Kirim Ulang</Text>
            </Button>
          </div>
        </div>
        <div style={stylesGlobal.wAuto}>
          <Image src={ENVELOPE_ICON} fluid style={styles.shareResend} />
        </div>
      </InformationBox>
    );
  }

  if (userNotKycYet) {
    return (
      <InformationBox
        gradient
        style={{
          ...styles.informationBox,
          ...stylesGlobal.w100,
          ...stylesGlobal.between,
        }}
      >
        <div
          style={{
            ...stylesGlobal.w100,
            ...stylesGlobal.dFlexColumn,
            ...stylesGlobal.top,
          }}
        >
          <div style={{ marginBottom: 4 }}>
            <Text size="section" bold color="inherit">
              Selesaikan Aktivasi Akun
            </Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text size="sub-section" color="inherit">
              Setelah akun aktif, kamu dapat berinvestasi saham atau reksa dana sesuai tujuan
              keuanganmu.
            </Text>
          </div>
          <div>
            <ButtonText
              style={styles.buttonUpgrade}
              bold
              text="Aktivasi Sekarang"
              textColor={stylesGlobal.primaryDark}
              hoverColor={stylesGlobal.primaryDark}
              hoverStyle={styles.buttonUpgradeHover}
              onClick={() => {
                window.open('/kyc/1', '_self');
                eventStartKyc();
              }}
            />
          </div>
        </div>
        <div style={stylesGlobal.wAuto}>
          <Image src={FILE_ICON} fluid style={styles.shareResend} />
        </div>
      </InformationBox>
    );
  }

  if (stockKycVerifying || mfKycVerifying || isCoinInprogress) {
    return (
      <InformationBox
        gradient
        style={{
          ...styles.informationBox,
          ...stylesGlobal.w100,
          ...stylesGlobal.between,
        }}
      >
        <div
          style={{
            ...stylesGlobal.w100,
            ...stylesGlobal.dFlexColumn,
            ...stylesGlobal.top,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <Text size="section" bold color="inherit">
              Data diri sedang diperiksa
            </Text>
          </div>
          <div>
            <Text size="sub-section" color="inherit">
              Tim kami sedang memastikan bahwa semua datamu benar.
            </Text>
          </div>
          <div>
            <Text size="sub-section" color="inherit">
              Proses pemeriksaan data memerlukan waktu 4-5 hari kerja (tidak termasuk Sabtu, Minggu,
              dan libur nasional).
            </Text>
          </div>
        </div>
        <div style={stylesGlobal.wAuto}>
          <Image src={CLOCK_ICON} style={styles.shareResend} />
        </div>
      </InformationBox>
    );
  }

  if (stockKycRejected && mfKycFailed) {
    return (
      <InformationBox
        gradient
        style={{
          ...styles.informationBox,
          ...stylesGlobal.w100,
          ...stylesGlobal.between,
        }}
      >
        <div
          style={{
            ...stylesGlobal.w100,
            ...stylesGlobal.dFlexColumn,
            ...stylesGlobal.top,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <Text size="section" bold color="inherit">
              Aktivasi Akun Gagal
            </Text>
          </div>
          <div>
            <Text size="sub-section" color="inherit">
              Ada data yang tidak sesuai sehingga kamu belum bisa investasi saham dan reksa dana.
            </Text>
          </div>
          <div style={stylesGlobal.left}>
            <Text size="sub-section" color="inherit">
              Untuk informasi lebih lanjut
            </Text>
            &nbsp;&nbsp;
            <Button
              variant="sentence"
              onClick={() => {
                window.open('https://ajaib.co.id/pusat-bantuan/', '_self');
              }}
            >
              <Text color="yellow">hubungi customer service</Text>
            </Button>
          </div>
        </div>
        <div style={stylesGlobal.wAuto}>
          <Image src={FAILED_KYC_ICON} style={styles.shareResend} />
        </div>
      </InformationBox>
    );
  }

  return (
    <InformationBox gradient style={styles.informationBox}>
      <div style={{ ...stylesGlobal.w100, paddingTop: 8, paddingBottom: 8 }}>
        <div style={{ ...stylesGlobal.w100, ...stylesGlobal.dFlex }}>
          <div style={stylesGlobal.flexOneAndHalf}>
            <div style={stylesGlobal.left}>
              <Text size="label-regular" color="inherit" style={{ marginRight: 8 }}>
                Total Investasi
              </Text>
              <Image
                src={isShowAsset ? EYE_OPEN_ICON : EYE_CLOSE_ICON}
                style={styles.eyeIcon}
                onClick={toggleIsShowAsset}
              />
            </div>
            <div>
              {isShowAsset ? (
                <Text size="h1" color="inherit">
                  <FormatRupiah value={homePortfolioData?.total_investasi ?? 0} />
                </Text>
              ) : (
                <Text size="h1" color="inherit">
                  *********
                </Text>
              )}
            </div>
          </div>

          <div style={stylesGlobal.flexTwo}>
            <div>
              <Text size="label-regular" color="inherit">
                Total Return
              </Text>
            </div>
            <div>
              {isShowAsset ? (
                <Text size="h1" color="inherit">
                  <FormatRupiah isSymbol value={homePortfolioData?.total_return ?? 0} />
                  &nbsp;(
                  <FormatPercentage
                    isSymbol
                    value={(homePortfolioData?.total_return_pct ?? 0) * 100}
                  />
                  )
                </Text>
              ) : (
                <Text size="h1" color="inherit">
                  *********
                </Text>
              )}
            </div>
          </div>

          <div style={{ ...stylesGlobal.flexOne, ...stylesGlobal.topRight }}>
            <Text color="white">
              Buying Power&nbsp;
              {isShowAsset ? (
                <Text color="white" bold>
                  <FormatRupiah value={buyingPowerData?.result?.trading_limit ?? 0} />
                </Text>
              ) : (
                <Text bold color="white">
                  *********
                </Text>
              )}
            </Text>
          </div>
        </div>

        {(stockKycRejected || mfKycRejected) && (
          <div style={{ ...stylesGlobal.left, marginTop: 4 }}>
            <Text size="sub-section" color="inherit">
              Aktivasi akun&nbsp;
              {stockKycRejected && mfKycVerified && 'saham'}
              {stockKycVerified && mfKycRejected && 'reksa dana'}
              {stockKycRejected && mfKycRejected && 'saham dan reksa dana'}
              &nbsp;gagal, untuk informasi lebih lanjut
            </Text>
            &nbsp;&nbsp;
            <Button
              variant="sentence"
              onClick={() => {
                window.open('https://ajaib.co.id/pusat-bantuan/', '_self');
              }}
            >
              <Text color="yellow">hubungi customer service</Text>
            </Button>
          </div>
        )}
      </div>
    </InformationBox>
  );
};

DashboardInvestmentSummary.propTypes = {
  isCoinInprogress: PropTypes.bool,
};

DashboardInvestmentSummary.defaultProps = {
  isCoinInprogress: false,
};

export default memo(DashboardInvestmentSummary);
